/**
 * Router accessor pattern to break circular dependencies.
 *
 * The router instance must be set via setRouter() in router/index.js
 * before any module attempts to access it via getRouter().
 *
 * This pattern prevents circular imports between router, store, and services.
 */

import type { Router, RouteLocationNormalized, RouteLocationRaw, NavigationFailure } from 'vue-router';

let routerInstance: Router | null = null;

/**
 * Stores the router instance for later access.
 * Must be called once during app initialization in router/index.js.
 * @param router - The Vue Router instance
 */
export function setRouter(router: Router): void {
  routerInstance = router;
}

/**
 * Retrieves the router instance.
 * @returns The Vue Router instance
 * @throws Error if router has not been initialized via setRouter()
 */
export function getRouter(): Router {
  if (!routerInstance) {
    throw new Error('Router not initialized. setRouter() must be called in router/index.js before navigation.');
  }
  return routerInstance;
}

/**
 * Gets the current active route.
 * @returns The current route location
 */
export function getCurrentRoute(): RouteLocationNormalized {
  return getRouter().currentRoute.value;
}

/**
 * Replaces the current route without adding to history.
 * @param to - The target route location
 * @returns A promise that resolves when navigation completes
 */
export function replaceRoute(to: RouteLocationRaw): Promise<void | NavigationFailure | undefined> {
  return getRouter().replace(to);
}

/**
 * Navigates to a new route, adding to history.
 * @param to - The target route location
 * @returns A promise that resolves when navigation completes
 */
export function pushRoute(to: RouteLocationRaw): Promise<void | NavigationFailure | undefined> {
  return getRouter().push(to);
}

let lastViewedBroadcastIdValue: string | null = null;

/**
 * Stores the ID of the last broadcast the user navigated away from.
 * Called by router/index.js when leaving a Broadcast route.
 * Used to show contextual "broadcast ended" messages on StageRoll.
 * @param id - The broadcast ID, or null to clear
 */
export function setLastViewedBroadcastId(id: string | null): void {
  lastViewedBroadcastIdValue = id;
}

/**
 * Gets the ID of the last broadcast the user viewed.
 * @returns The last viewed broadcast ID, or null if none
 */
export function getLastViewedBroadcastId(): string | null {
  return lastViewedBroadcastIdValue;
}
